import React, { useState, useEffect, useRef, useCallback } from "react";
import useDarkMode from '../../../hooks/useDarkMode';
import useThemeColor from "../../../hooks/useThemeColor";
import { useSelector } from "react-redux";
import axiosInstance from "../../../config/axiosApi/axiosApi";
import ToggleSwitch from "../Switchery/switchery";
import AllergenSelectionModal from "./allergenSelectionModal/allergenSelectionModal";


function AllergenSelector({
  isEnabled,
  setIsEnabled,
  selectedAllergens,
  setSelectedAllergens,
  showModal,
  setShowModal,
  onModalSubmit,
  setLoading,
}) {
  const [allergens, setAllergens] = useState([]);
  const [hasLoadedData, setHasLoadedData] = useState(false);
  const isDarkMode = useDarkMode();
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  // Get theme color from centralized hook
  const themeColor = useThemeColor();

  // Safeguard: Ensure selectedAllergens is an array
  const safeSelectedAllergens = Array.isArray(selectedAllergens) ? selectedAllergens : [];

  const fetchAllergens = useCallback(async () => {
    if (hasLoadedData) return;

    try {
      setLoading(true);
      const response = await axiosInstance.get("/api/Allergen/GetAllAllergensGlobal");
      const allergenData = response.data.data || [];
      if (Array.isArray(allergenData)) {
        setAllergens(allergenData);
        setHasLoadedData(true);
      } else {
        setAllergens([]);
      }
    } catch (err) {
      console.error("Error fetching allergens:", err);
      setAllergens([]);
    } finally {
      setLoading(false);
    }
  }, [hasLoadedData, setLoading]);

  useEffect(() => {
    if (showModal && !hasLoadedData) {
      fetchAllergens();
    }
  }, [showModal, hasLoadedData, fetchAllergens]);

  

  const removeAllergen = (allergenId) => {
    const stringAllergenId = String(allergenId);
    const updatedAllergens = safeSelectedAllergens.filter(
      (a) => String(a.id) !== stringAllergenId
    );
    setSelectedAllergens(updatedAllergens);
  };

  const handleModalSubmit = (selected) => {
    if (typeof onModalSubmit === "function") {
      onModalSubmit(selected);
    }
    setSelectedAllergens(selected);
    setShowModal(false);
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    dragItem.current = index;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("application/json", JSON.stringify(index));
    e.currentTarget.style.opacity = "0.5";
    e.currentTarget.style.cursor = "grabbing";
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    dragOverItem.current = index;
    e.currentTarget.style.backgroundColor = "#e9ecef";
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.currentTarget.style.backgroundColor = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const draggedIndex = JSON.parse(e.dataTransfer.getData("application/json"));
    const overIndex = dragOverItem.current;

    if (
      draggedIndex === overIndex ||
      draggedIndex === undefined ||
      overIndex === undefined
    ) {
      dragItem.current = null;
      dragOverItem.current = null;
      return;
    }

    const newAllergens = [...safeSelectedAllergens];
    const draggedAllergen = newAllergens[draggedIndex];
    newAllergens.splice(draggedIndex, 1);
    newAllergens.splice(overIndex, 0, draggedAllergen);

    setSelectedAllergens(newAllergens);
    dragItem.current = null;
    dragOverItem.current = null;
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = "1";
    e.currentTarget.style.cursor = "grab";
    document.querySelectorAll(".allergen-chip").forEach((chip) => {
      chip.style.backgroundColor = "";
    });
    dragItem.current = null;
    dragOverItem.current = null;
  }

  return (
    <div 
      className="card p-3"
      style={{
        backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
        border: isDarkMode ? '1px solid #404040' : '1px solid #dee2e6',
        color: isDarkMode ? '#ffffff' : '#000000',
      }}
    >
      {/* Toggle switch removed - content always visible */}
      <div className="mt-3">
          {safeSelectedAllergens.length > 0 ? (
            <>
              <div className="row row-cols-1 row-cols-md-2 g-3">
                {safeSelectedAllergens.map((allergen, index) => (
                  <div key={allergen.id || `allergen-${allergen.allergen_Name}-${index}`} className="col">
                    <div
                      className="allergen-chip"
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragEnter={(e) => handleDragEnter(e, index)}
                      onDragLeave={handleDragLeave}
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                      onDragEnd={handleDragEnd}
                      style={{
                        background: isDarkMode ? '#404040' : '#ffffff',
                        borderRadius: '12px',
                        padding: '16px 20px',
                        border: isDarkMode ? '2px solid #555' : '2px solid #e9ecef',
                        transition: 'all 0.2s ease',
                        cursor: 'grab',
                        position: 'relative',
                        boxShadow: isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.05)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = themeColor;
                        e.currentTarget.style.boxShadow = `0 4px 12px ${themeColor}25`;
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = isDarkMode ? '#555' : '#e9ecef';
                        e.currentTarget.style.boxShadow = isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.05)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                          {/* Drag Handle */}
                          <div
                            style={{
                              width: '20px',
                              height: '20px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              marginRight: '8px',
                              cursor: 'grab',
                              color: isDarkMode ? '#ffffff' : '#6c757d',
                              borderRadius: '4px',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = isDarkMode ? '#555' : '#f8f9fa';
                              e.currentTarget.style.color = isDarkMode ? '#ffffff' : '#495057';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'transparent';
                              e.currentTarget.style.color = isDarkMode ? '#ffffff' : '#6c757d';
                            }}
                          >
                            <i className="fa fa-bars" style={{ fontSize: '12px' }}></i>
                          </div>
                          {/* Allergen Icon */}
                          <div
                            style={{
                              width: '36px',
                              height: '36px',
                              background: isDarkMode ? '#555' : '#f8f9fa',
                              borderRadius: '8px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              marginRight: '12px',
                              border: isDarkMode ? '1px solid #666' : '1px solid #e9ecef'
                            }}
                          >
                            <i
                              className="fa fa-exclamation-triangle"
                              style={{ fontSize: '14px', color: isDarkMode ? '#ffffff' : '#6c757d' }}
                            ></i>
                          </div>
                          {/* Allergen Name */}
                          <div>
                            <span
                              style={{
                                fontSize: '15px',
                                fontWeight: '500',
                                color: isDarkMode ? '#ffffff' : '#495057'
                              }}
                            >
                              {allergen.allergen_Name}
                            </span>
                          </div>
                        </div>
                        {/* Remove Button */}
                        <button
                          onClick={() => removeAllergen(allergen.id)}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            borderRadius: '6px',
                            width: '28px',
                            height: '28px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            color: '#dc3545'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = isDarkMode ? '#4a1a1a' : '#f8d7da';
                            e.currentTarget.style.color = isDarkMode ? '#ff6b6b' : '#721c24';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent';
                            e.currentTarget.style.color = '#dc3545';
                          }}
                        >
                          <i
                            className="fa fa-times"
                            style={{
                              fontSize: '12px'
                            }}
                          ></i>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="d-flex justify-content-center mt-4">
                <button
                  onClick={() => {
                    if (!hasLoadedData) fetchAllergens();
                    setShowModal(true);
                  }}
                  style={{
                    background: themeColor,
                    border: 'none',
                    borderRadius: '8px',
                    padding: '10px 20px',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = themeColor + 'dd';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = themeColor;
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <i className="fa-solid fa-plus" style={{ fontSize: '12px' }}></i>
                  Choose More Allergens
                </button>
              </div>
            </>
          ) : (
            <div className="text-center py-4">
              <div
                style={{
                  width: '60px',
                  height: '60px',
                  background: isDarkMode ? '#555' : '#f8f9fa',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  border: isDarkMode ? '1px solid #666' : '1px solid #e9ecef'
                }}
              >
                <i
                  className="fa fa-exclamation-triangle"
                  style={{ fontSize: '24px', color: isDarkMode ? '#ffffff' : '#6c757d' }}
                ></i>
              </div>
              <p style={{ color: isDarkMode ? '#ffffff' : '#6c757d', fontSize: '15px', marginBottom: '20px' }}>
                No allergens selected
              </p>
              <button
                onClick={() => {
                  if (!hasLoadedData) fetchAllergens();
                  setShowModal(true);
                }}
                style={{
                  background: themeColor,
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px 20px',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  margin: '0 auto'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = themeColor + 'dd';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = themeColor;
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <i className="fa-solid fa-plus" style={{ fontSize: '12px' }}></i>
                Choose Allergens
              </button>
            </div>
          )}
        </div>
      <AllergenSelectionModal
        showModal={showModal}
        setShowModal={setShowModal}
        onSubmit={handleModalSubmit}
        selectedAllergens={safeSelectedAllergens}
        setSelectedAllergens={setSelectedAllergens}
        allergens={allergens}
      />
    </div>
  );
}

export default AllergenSelector;